import {
  Bundesland,
  EndpointMode,
  StationFilterEndpointMode,
  StationMetadataDto,
} from '@/api/out';
import { ApiStore } from '@/stores/api-store';
import DatasetStore from '@/stores/dataset/dataset-module';
import { getMockDataWithFilter } from '@/stores/station/station-mock';
import {
  Filters,
  Station,
  StationFilterResponse,
} from '@/stores/station/station-model';
import store from '@/stores/station/station-store';
import { formatDateForQuery } from '@/util/datetime';
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import { filterUndefined } from '@/util/object';
import { StationTableColumnMenuItem } from '@/stores/stationTable/station-table-model';

export function copyFilters(filters: Filters): Filters {
  // deep copy for filter object
  return filterUndefined({
    ...filters,
    altitude: filters.altitude ? [...filters.altitude] : undefined,
  });
}

export function filtersAreEqual(filters1: Filters, filters2: Filters): boolean {
  return JSON.stringify(filters1) === JSON.stringify(filters2);
}

@Module({ dynamic: true, namespaced: true, name: 'StationModule', store })
class StationModule extends VuexModule {
  private _stations: Station[] = [];
  private _isLoading = false;
  private _selectedStations: Station[] = [];
  private _filters: Filters = {};
  private _totalNumberOfStations = 0;
  private _totalNumberOfMatchingStations = 0;
  private _availableHeadersForMenu: StationTableColumnMenuItem[] = this.defaultVisibleHeadersInMenu;
  private _visibleHeadersInMenu: string[] = this.defaultVisibleHeadersInMenu.map(h => h.value);

  public get selectedStations(): Station[] {
    return this._selectedStations;
  }

  public get selectedStationsIds(): Set<string> {
    return new Set<string>(this.selectedStations.map((station) => station.id));
  }

  public get stationsAreSelected(): boolean {
    return this.selectedStations.length > 0;
  }

  public get filtersAreSet(): boolean {
    return Object.keys(this._filters).length > 0;
  }

  public get filters(): Filters {
    return this._filters;
  }

  public get allStations(): Station[] {
    return this._stations;
  }

  public get getAllStationCount(): number {
    return this._totalNumberOfStations;
  }

  public get getMatchingStationCount(): number {
    return this._totalNumberOfMatchingStations;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get isStationSelected(): (station: Station) => boolean {
    return (station: Station) =>
      this._selectedStations.some((s) => s.id === station.id);
  }

  public get getVisibleHeaders(): string[] {
    return this._visibleHeadersInMenu;
  }

  public get getAvailableHeadersForMenu(): StationTableColumnMenuItem[] {
    return this._availableHeadersForMenu;
  }

  public get getAvailableStations(): Station[] {
    return this._stations.filter((item) => !this.isStationSelected(item));
  }

  public get getNumberOfAvailableStations(): number {
    return this.getAvailableStations.length;
  }

  public get defaultVisibleHeadersInMenu(): StationTableColumnMenuItem[] {
    return [
      'count',
      'id',
      'name',
      'startDate',
      'endDate',
      'state',
      'altitude',
      'hasSunshine',
      'hasGlobalRadiation',
    ].map((name) => {
      return {
        value: name,
        checked: true,
        text: name,
      };
    });
  }

  @Mutation
  updateSelectedStations(stations: Station[]): void {
    this._selectedStations = stations;
  }

  @Mutation
  updateFilters(filters: Filters): void {
    this._filters = copyFilters(filters);
  }

  @Mutation
  updateStations(response: StationFilterResponse): void {
    this._stations = response.stations;
    this._totalNumberOfStations = response.numStationsTotal;
    this._totalNumberOfMatchingStations = response.numStationsMatching;
  }

  @Mutation
  updateLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  @Action({ commit: 'updateSelectedStations', rawError: true })
  setSelectedStations(stations: Station[]): Station[] {
    return stations;
  }

  @Action({ commit: 'updateSelectedStations', rawError: true })
  removeStationsFromSelected(stationsToRemove: Station[]): Station[] {
    const idsToRemove = stationsToRemove.map((station) => station.id);
    return this._selectedStations.filter(
      (station) => !idsToRemove.includes(station.id)
    );
  }

  @Action({ commit: 'updateSelectedStations', rawError: true })
  addStationsToSelected(stations: Station[]): Station[] {
    const selectedStations = [...this._selectedStations];
    stations.forEach((station) => {
      if (!this.isStationSelected(station)) {
        selectedStations.push(station);
      }
    });
    return selectedStations;
  }

  @Action({ rawError: true })
  toggleStationIsSelected(station: Station): void {
    if (this.isStationSelected(station)) {
      this.removeStationsFromSelected([station]);
    } else {
      this.addStationsToSelected([station]);
    }
  }

  @Mutation
  updateVisibleHeaders(): void {
    this._visibleHeadersInMenu = this._availableHeadersForMenu
      .filter((item) => item.checked)
      .map((item) => item.value);
  }

  @Mutation
  updateAvailableHeadersForMenu(headers: StationTableColumnMenuItem[]): void {
    this._availableHeadersForMenu = headers;
    this._visibleHeadersInMenu = headers.map((item) => item.value);
  }

  @Action({ commit: 'updateAvailableHeaders', rawError: true })
  visibleHeadersChange(): StationTableColumnMenuItem[] {
    return this._availableHeadersForMenu;
  }

  @Action({ commit: 'updateVisibleHeaders', rawError: true })
  clickStationTableColumnHeaderMenuItem(): StationTableColumnMenuItem[] {
    return this._availableHeadersForMenu.filter((item) => item.checked);
  }

  @Action
  putFilter(filter: Partial<Filters>): void {
    // TODO: mutation should not be called directly but rather committed, Issue #56
    this.updateFilters({
      ...copyFilters(this.filters),
      ...filter,
    });
  }

  @Action
  async fetchFilteredStations() {
    await this.fetchFilteredStationsFromApi();
    // await this.fetchFilteredStationsFromMock();
  }

  @Action({ rawError: true })
  async fetchFilteredStationsFromMock(): Promise<void> {
    this.updateLoading(true);
    const stationDataExternal = await getMockDataWithFilter(this.filters);
    const stationData: Station[] = stationDataExternal.map((station) =>
      parseExternalStation(station)
    );

    this.context.commit('updateStations', stationData);
    this.updateLoading(false);
  }

  @Action({ commit: 'updateStations', rawError: true })
  async fetchFilteredStationsFromApi(): Promise<StationFilterResponse> {
    const dataset = DatasetStore.metadata?.datasetParams;
    if (!dataset) {
      return {
        numStationsTotal: 0,
        numStationsMatching: 0,
        stations: [],
      };
    }
    if (!dataset.resourceId || !dataset.mode) {
      throw Error(
        'Resource ID and endpoint mode are required to fetch stations'
      );
    }

    const filters = this.filters;
    const args = {
      ids: filters.id ? filters.id.split(',') : undefined,
      names: filters.name ? filters.name.split(',') : undefined,
      states: filters.states
        ? filters.states.map((b) => b as Bundesland)
        : undefined,
      startDate: filters.startDate
        ? formatDateForQuery(filters.startDate)
        : undefined,
      endDate: filters.endDate
        ? formatDateForQuery(filters.endDate)
        : undefined,
      minAltitude: filters.altitude ? filters.altitude[0] : undefined,
      maxAltitude: filters.altitude ? filters.altitude[1] : undefined,
      onlyWithSunshine: filters.onlyWithSunshine,
      onlyWithGlobalRadiation: filters.onlyWithGlobalRadiation,
      onlyCurrent: filters.onlyCurrent,
    };

    this.updateLoading(true);

    function parseEndpointModeToFilterMode(
      mode: EndpointMode
    ): StationFilterEndpointMode {
      switch (mode) {
        case EndpointMode.Current:
          return StationFilterEndpointMode.Current;
        case EndpointMode.Historical:
          return StationFilterEndpointMode.Historical;
        default:
          return StationFilterEndpointMode.Historical;
      }
    }

    const response =
      await ApiStore.dataApi.stationApi.filterStationsStationModeResourceIdFilterGet(
        dataset.resourceId,
        parseEndpointModeToFilterMode(dataset.mode),
        args.ids,
        args.names,
        args.states,
        args.startDate,
        args.endDate,
        args.minAltitude,
        args.maxAltitude,
        args.onlyWithSunshine,
        args.onlyWithGlobalRadiation,
        args.onlyCurrent
      );
    this.updateLoading(false);

    return {
      numStationsTotal: response.data.num_stations_total,
      numStationsMatching: response.data.num_stations_matching,
      stations: response.data.matching_stations.map((station) =>
        parseExternalStation(station)
      ),
    };
  }
}

function parseExternalStation(apiStation: StationMetadataDto): Station {
  const subStations: Station[] =
    apiStation.sub_stations === undefined
      ? []
      : apiStation.sub_stations.map((subStation) =>
          parseExternalStation(subStation)
        );
  return {
    id: apiStation.id,
    name: apiStation.name,
    startDate: new Date(apiStation.valid_from),
    endDate: new Date(apiStation.valid_to),
    lat: apiStation.lat,
    lon: apiStation.lon,
    state: apiStation.state,
    altitude: apiStation.altitude,
    hasSunshine: apiStation.has_sunshine,
    hasGlobalRadiation: apiStation.has_global_radiation,
    subStations,
    matchesFilter: apiStation.matches,
    type: apiStation.type,
    count: subStations.length > 0 ? subStations.length : 1,
    isActive: apiStation.is_active,
  };
}

export default getModule(StationModule);
